import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ShareButtons } from "@/components/ShareButtons";
import { useReveal } from "@/hooks/use-reveal";
import { getPost, POSTS_BY_DATE, formatDate } from "@/content/blog";
import { POST_ANSWERS } from "@/content/answers";


export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = getPost(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ params, loaderData }) => {
    const post = loaderData?.post;
    if (!post) {
      return {
        meta: [{ title: "Note not found — The Founders App" }, { name: "robots", content: "noindex" }],
      };
    }
    const url = `https://thefoundersapp.com/blog/${params.slug}`;
    const answers = POST_ANSWERS[post.slug];

    return {
      meta: [
        { title: `${post.metaTitle} | The Founders App` },
        { name: "description", content: post.description },
        { property: "og:title", content: post.title },
        { property: "og:description", content: post.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "article:published_time", content: post.published },
        { property: "article:section", content: post.category },
        { property: "og:image", content: "https://thefoundersapp.com/og-cover.jpg" },
        { name: "twitter:image", content: "https://thefoundersapp.com/og-cover.jpg" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.description,
            datePublished: post.published,
            dateModified: post.updated ?? post.published,
            articleSection: post.category,
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            author: { "@type": "Organization", name: "Skylab Studio LLC" },
            publisher: {
              "@type": "Organization",
              name: "Skylab Studio LLC",
              areaServed: "San Francisco Bay Area, California",
            },
            ...(answers ? { abstract: answers.tldr } : {}),
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", "[data-answer]"],
            },
          }),
        },
        ...(answers
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: answers.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }),
              },
            ]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "/" },
              { "@type": "ListItem", position: 2, name: "Blog", item: "/blog" },
              { "@type": "ListItem", position: 3, name: post.title, item: url },
            ],
          }),
        },
      ],

    };
  },
  component: BlogPost,
  notFoundComponent: PostNotFound,
});

function PostNotFound() {
  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="aurora" aria-hidden />
      <SiteHeader />
      <main id="main" className="relative mx-auto w-full max-w-3xl px-6 pt-24 pb-24 sm:px-10">
        <h1 className="etched text-3xl font-semibold">That note isn't here.</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          It may have moved. The rest of the field notes are still on the index.
        </p>
        <Link to="/blog" className="micro-label mt-8 inline-flex items-center gap-2">
          <ArrowLeft className="h-3.5 w-3.5" /> All notes
        </Link>
      </main>
      <SiteFooter />
    </div>
  );
}

function BlogPost() {
  useReveal();
  const { post } = Route.useLoaderData();
  const answers = POST_ANSWERS[post.slug];

  const related = POSTS_BY_DATE.filter((p) => post.relatedSlugs?.includes(p.slug));
  const others = POSTS_BY_DATE.filter(
    (p) => p.slug !== post.slug && !post.relatedSlugs?.includes(p.slug),
  ).slice(0, Math.max(0, 3 - related.length));
  const keepReading = [...related, ...others];

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="aurora" aria-hidden />
      <SiteHeader />

      <main id="main" className="relative mx-auto w-full max-w-3xl px-6 pt-16 pb-24 sm:px-10">
        <nav aria-label="Breadcrumb" className="reveal">
          <Link
            to="/blog"
            className="micro-label inline-flex items-center gap-2 transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Field notes
          </Link>
        </nav>

        <div className="reveal mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 [--d:60ms]">
          <span className="micro-label">{post.category}</span>
          <span className="text-xs text-muted-foreground/60">
            <time dateTime={post.published}>{formatDate(post.published)}</time> ·{" "}
            {post.readMinutes} min read
          </span>
        </div>

        <h1 className="reveal etched mt-5 text-[2.1rem] leading-[1.02] font-semibold sm:text-5xl [--d:100ms]">
          {post.title}
        </h1>
        <p className="reveal mt-6 text-[0.95rem] leading-relaxed text-muted-foreground [--d:160ms]">
          {post.excerpt}
        </p>

        {answers ? (
          <section
            aria-labelledby="short-answer"
            className="reveal soft-panel mt-10 p-6 [--d:200ms]"
          >
            <h2 id="short-answer" className="micro-label">
              Short answer
            </h2>
            <p
              data-answer
              className="mt-3 text-[0.95rem] leading-relaxed text-foreground/90"
            >
              {answers.tldr}
            </p>
          </section>
        ) : null}


        <div className="hairline scan-rule mt-12" />

        <article className="mt-10 space-y-6">
          {post.blocks.map((block, i) => {
            const key = `${block.type}-${i}`;
            if (block.type === "h2") {
              return (
                <h2 key={key} className="reveal pt-6 text-xl font-medium sm:text-2xl">
                  {block.text}
                </h2>
              );
            }
            if (block.type === "quote") {
              return (
                <blockquote
                  key={key}
                  className="reveal border-l border-foreground/25 py-1 pl-5 text-[0.95rem] leading-relaxed text-foreground/85 italic"
                >
                  {block.text}
                </blockquote>
              );
            }
            if (block.type === "list") {
              return (
                <ul key={key} className="reveal space-y-3">
                  {block.items.map((item) => (
                    <li
                      key={item.slice(0, 28)}
                      className="flex gap-3 text-sm leading-relaxed text-muted-foreground"
                    >
                      <span aria-hidden className="mt-2 h-px w-4 shrink-0 bg-foreground/30" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              );
            }
            return (
              <p key={key} className="reveal text-[0.95rem] leading-relaxed text-muted-foreground">
                {block.text}
              </p>
            );
          })}
        </article>

        {answers ? (
          <section aria-labelledby="post-faq" className="mt-16">
            <h2 id="post-faq" className="reveal text-xl font-medium sm:text-2xl">
              Frequently asked questions
            </h2>
            <dl className="mt-6 space-y-6">
              {answers.faqs.map((f) => (
                <div key={f.q} className="reveal">
                  <dt className="text-[0.95rem] font-medium text-foreground/90">{f.q}</dt>
                  <dd
                    data-answer
                    className="mt-2 text-[0.95rem] leading-relaxed text-muted-foreground"
                  >
                    {f.a}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        <div className="hairline mt-14" />


        <ShareButtons
          className="reveal mt-8"
          url={`/blog/${post.slug}`}
          title={post.title}
          label="Pass it on"
        />

        <section className="reveal soft-panel mt-8 p-8">
          <p className="micro-label">Try The Founders App</p>
          <h2 className="mt-4 text-xl font-medium">Run this loop tomorrow morning.</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            The Founders App is a PIN-locked operating system for every business you run: one move a day, one
            bold move a month, a private journal, a contact log with memory, and one timeline that
            holds all of it.

          </p>
          <Link
            to="/pricing"
            className="micro-label mt-6 inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2.5 transition-colors hover:border-foreground/40 hover:text-foreground"
          >
            See plans <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </section>

        {others.length > 0 && (
          <section className="mt-14">
            <p className="reveal micro-label">Keep reading</p>
            <div className="mt-5 space-y-3">
            {keepReading.map((p) => (
                <Link
                  key={p.slug}
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="reveal soft-panel block p-6 transition-colors hover:border-foreground/25"
                >
                  <span className="micro-label">{p.category}</span>
                  <span className="mt-3 block text-base font-medium">{p.title}</span>
                  <span className="mt-2 block text-sm leading-relaxed text-muted-foreground">
                    {p.excerpt}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
